import React, { useMemo } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowDown from '../../../../Icons/16x16/ArrowDown';
import { IconSize } from '../../../../Icons/IconWrap';
import noop from 'lodash/noop';
import NativeDropdown, { type NativeDropdownProps } from '../../../NativeDropdown';
import { DropdownOption } from '../../../types';
import s from './SelectFieldContent.scss';

function isPlaceholderValue(value: React.SelectHTMLAttributes<any>['value']) {
  return value === undefined || value === '';
}

export interface Props extends Omit<NativeDropdownProps, 'className' | 'prefix'> {
  /** Required for a11y reasons. We need to be able to link auto-complete menus and labels to the input field. */
  id: string;
  className?: string;
  items: DropdownOption[];
  withBlankOption?: boolean;
  prefix?: React.ReactNode;
  hasError?: boolean;
  isOpen?: boolean;
  placeholder?: string;
  /**
   * The selected value's label, by default, is displayed the same as it appears in the native dropdown (i.e. {@link DropdownOption.label}). This flag
   * allows the selected value's label to use {@link DropdownOption.complexLabel}.
   *
   * **Note: This does not change the rendering of labels inside the native dropdown.**
   */
  allowComplexLabelOnSelected?: boolean;
}

const SelectFieldContent: React.FC<Props> = ({
  items,
  value,
  withBlankOption = true,
  id,
  placeholder: placeholderProp,
  allowComplexLabelOnSelected,
  disabled,
  prefix,
  onSelect = noop,
  hasError,
  ...rest
}) => {
  const { t } = useTranslation();
  const selectListId = `${id}-list`;
  const placeholder = placeholderProp ?? t('common.select');

  const valueLabel = useMemo(() => {
    const selectedItem = items.find(item => `${item.value}` === `${value}`);
    if (!selectedItem) {
      return placeholder;
    }
    if (allowComplexLabelOnSelected) {
      return selectedItem.complexLabel ?? selectedItem.label ?? placeholder;
    } else {
      return selectedItem.label ?? placeholder;
    }
  }, [items, allowComplexLabelOnSelected, value, placeholder]);

  const options = useMemo(
    () => [
      ...(withBlankOption ? [{ label: placeholder, value: '' }] : []),
      ...items.map(item => ({
        ...item,
        selected: `${item.value}` === `${value}`,
      })),
    ],
    [withBlankOption, items, value, placeholder]
  );

  return (
    <div className={c(s['root'], hasError && s['error'], disabled && s['disabled'])}>
      <div
        id={id}
        className={c(s['toggle'], 'ignore-react-onclickoutside')}
        role="menu"
        aria-controls={selectListId}
      >
        {prefix && <div className={s['prefix']}>{prefix}</div>}
        {!isPlaceholderValue(value) && <div className={s['value']}>{valueLabel}</div>}
        {isPlaceholderValue(value) && placeholder && (
          <div className={s['placeholder']}>{placeholder}</div>
        )}
        <ArrowDown className={s['icon']} size={IconSize.SMALLER} disableDefaultColors />
        <div className={s['native-dropdown-hidden']}>
          <NativeDropdown items={options} placeholder={placeholder} onSelect={onSelect} {...rest} />
        </div>
      </div>
    </div>
  );
};

SelectFieldContent.displayName = 'SelectFieldContent';

export default SelectFieldContent;
